import { useState, useEffect, useCallback } from 'react';
import { PiCaretDoubleDownBold } from 'react-icons/pi';
import { MdFilterList } from 'react-icons/md'; 
import { useSession } from '../../context/SessionContext';

export default function ProductSideBar({
    selectedCategories = [],
    selectedBrands = [],
    selectedModels = [],
    onFilterChange,
}) {
    const { productTypes = [], brands = [], models = [] } = useSession();

    const [localCategories, setLocalCategories] = useState(selectedCategories);
    const [localBrands, setLocalBrands] = useState(selectedBrands);
    const [localModels, setLocalModels] = useState(selectedModels);

    useEffect(() => {
        setLocalCategories(selectedCategories);
        setLocalBrands(selectedBrands);
        setLocalModels(selectedModels);
    }, [selectedCategories, selectedBrands, selectedModels]);

    const toggle = useCallback((type, id) => {
        const update = (current) => {
            const set = new Set(current);
            set.has(id) ? set.delete(id) : set.add(id);
            return Array.from(set);
        };

        if (type === 'category') {
            const newVals = update(localCategories);
            setLocalCategories(newVals);
            onFilterChange('product_type', newVals);
        } else if (type === 'brand') {
            const newVals = update(localBrands);
            setLocalBrands(newVals);
            onFilterChange('brand', newVals);
        } else if (type === 'model') {
            const newVals = update(localModels);
            setLocalModels(newVals);
            onFilterChange('model', newVals);
        }
    }, [localCategories, localBrands, localModels, onFilterChange]);

    const isChecked = (type, id) => {
        if (type === 'category') return localCategories.includes(id);
        if (type === 'brand') return localBrands.includes(id);
        if (type === 'model') return localModels.includes(id);
        return false;
    };

    return (
        <div className="sticky top-4">
            {/* Main Floating Container */}
            <div className="bg-white dark:bg-slate-900 p-5 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800">
                
                {/* Modern Header */}
                <div className="flex items-center justify-between pb-4 mb-2 border-b border-gray-100 dark:border-gray-800">
                    <h3 className="flex items-center gap-2 text-lg font-bold text-gray-900 dark:text-white">
                        <MdFilterList className="text-primary text-2xl" />
                        Filters
                    </h3>
                    <button
                        onClick={() => onFilterChange(null, { clear: true })}
                        className="text-xs font-semibold text-primary bg-primary/10 hover:bg-primary/20 px-3 py-1.5 rounded-full transition-colors"
                        type="button"
                    >
                        Clear All
                    </button>
                </div>

                {/* Accordions Container */}
                <div className="space-y-1">
                    
                    {/* Category */}
                    {productTypes.length > 0 && (
                        <details
                            className="group py-3 border-b border-gray-100 dark:border-gray-800 last:border-b-0"
                            open={!!selectedCategories.length}
                        >
                            <summary className="flex cursor-pointer items-center justify-between gap-4 select-none list-none [&::-webkit-details-marker]:hidden">
                                <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 group-open:text-primary transition-colors">
                                    Category
                                </p>
                                <PiCaretDoubleDownBold className="h-4 w-4 text-gray-400 transition-transform duration-300 group-open:rotate-180 group-open:text-primary" />
                            </summary>
                            <div className="pt-3 space-y-1">
                                {productTypes.map((type) => (
                                    <label key={type.id} className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors group/item">
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-0 checked:bg-primary checked:border-primary transition-all"
                                            checked={isChecked('category', type.id)}
                                            onChange={() => toggle('category', type.id)}
                                        />
                                        <span className="text-sm text-gray-700 dark:text-gray-300 font-medium flex-1 group-hover/item:text-primary transition-colors">
                                            {type.name}
                                        </span>
                                        {type.product_count !== undefined && (
                                            <span className="text-[10px] font-bold bg-gray-100 dark:bg-slate-800 text-gray-500 dark:text-gray-400 px-2 py-0.5 rounded-full">
                                                {type.product_count}
                                            </span>
                                        )}
                                    </label>
                                ))}
                            </div>
                        </details>
                    )}

                    {/* Brand */}
                    {brands.length > 0 && (
                        <details
                            className="group py-3 border-b border-gray-100 dark:border-gray-800 last:border-b-0"
                            open={!!selectedBrands.length}
                        >
                            <summary className="flex cursor-pointer items-center justify-between gap-4 select-none list-none [&::-webkit-details-marker]:hidden">
                                <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 group-open:text-primary transition-colors">
                                    Brand
                                </p>
                                <PiCaretDoubleDownBold className="h-4 w-4 text-gray-400 transition-transform duration-300 group-open:rotate-180 group-open:text-primary" />
                            </summary>
                            <div className="pt-3 space-y-1">
                                {brands.map((brand) => (
                                    <label key={brand.id} className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors group/item">
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-0 checked:bg-primary checked:border-primary transition-all"
                                            checked={isChecked('brand', brand.id)}
                                            onChange={() => toggle('brand', brand.id)}
                                        />
                                        <span className="text-sm text-gray-700 dark:text-gray-300 font-medium flex-1 group-hover/item:text-primary transition-colors">
                                            {brand.name}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </details>
                    )}

                    {/* Model */}
                    {models.length > 0 && (
                        <details
                            className="group py-3 border-b border-gray-100 dark:border-gray-800 last:border-b-0"
                            open={!!selectedModels.length}
                        >
                            <summary className="flex cursor-pointer items-center justify-between gap-4 select-none list-none [&::-webkit-details-marker]:hidden">
                                <p className="text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400 group-open:text-primary transition-colors">
                                    Model
                                </p>
                                <PiCaretDoubleDownBold className="h-4 w-4 text-gray-400 transition-transform duration-300 group-open:rotate-180 group-open:text-primary" />
                            </summary>
                            <div className="pt-3 space-y-1">
                                {models.map((model) => (
                                    <label key={model.id} className="flex items-center gap-3 p-2 -mx-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-800/50 cursor-pointer transition-colors group/item">
                                        <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary focus:ring-0 checked:bg-primary checked:border-primary transition-all"
                                            checked={isChecked('model', model.id)}
                                            onChange={() => toggle('model', model.id)}
                                        />
                                        <span className="text-sm text-gray-700 dark:text-gray-300 font-medium flex-1 group-hover/item:text-primary transition-colors">
                                            {model.name}
                                        </span>
                                    </label>
                                ))}
                            </div>
                        </details>
                    )}
                </div>

                {/* Premium Apply Button */}
                <button
                    onClick={() => {
                        // Optional: trigger apply if backend needs explicit signal
                    }}
                    className="w-full mt-6 bg-primary hover:bg-red-800 text-white py-3.5 rounded-xl font-bold text-sm transition-all shadow-md hover:shadow-lg hover:shadow-primary/20 flex items-center justify-center gap-2"
                    type="button"
                >
                    Apply Filters
                </button>
            </div>
        </div>
    );
}